import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store";
import DenmarkEditModal from "./DenmarkEditModal";
import { createHostOptions } from "@/utils";
import {
  getDenmarkSectionList,
  getDenmarkChildSectionList,
  getDenmarkTaxonomyList,
  fetchPriorityList,
  fetchHostSection,
} from "@/store/media/media.action";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";

interface DenmarkPriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTeam: any;
}

/**
 * Self-contained wrapper that opens DenmarkEditModal in priority-only mode.
 * Connects to Redux for hosts, sections, and priority data so the parent
 * (e.g. Header) doesn't need to wire up all the Denmark props.
 */
const DenmarkPriorityModal: React.FC<DenmarkPriorityModalProps> = ({
  isOpen,
  onClose,
  defaultTeam,
}) => {
  const dispatch = useDispatch();

  const hosts = useSelector((state: RootState) => state.media.hostList.list);
  const hostSubsection = useSelector(
    (state: RootState) => state.media.hostSubSection.list
  );
  const allHostsList = useSelector(
    (state: RootState) => state.constants.allHostsList.list
  );
  const denmarkHosts: any[] = (() => {
    if (allHostsList && Array.isArray((allHostsList as any)?.hosts)) {
      return (allHostsList as any).hosts;
    }
    if (Array.isArray(allHostsList)) return allHostsList;
    return [];
  })();
  const denmarkTaxonomyList = useSelector(
    (state: RootState) => state.media.denmarkTaxonomyList.list
  );
  const denmarkSectionList = useSelector(
    (state: RootState) => state.media.denmarkSectionList.list
  );
  const priorityList = useSelector(
    (state: RootState) => state.media.priorityList.list
  );

  const hostOptions = createHostOptions(hosts, denmarkHosts);

  const [selectedHost, setSelectedHost] = useState<string>(
    defaultTeam?.settings?.host?.toString() ?? ""
  );

  useEffect(() => {
    if (defaultTeam?.settings?.host) {
      setSelectedHost(defaultTeam.settings.host.toString());
    }
  }, [defaultTeam?.settings?.host]);

  const handleHostChange = useCallback((host: any) => {
    const hostId = host?.value?.toString() ?? "";
    setSelectedHost(hostId);
  }, []);

  const handleFetchHostSection = useCallback(
    (params: any) => {
      dispatch(fetchHostSection(params) as any);
    },
    [dispatch]
  );

  // Empty publishData — not needed in priority-only mode
  const emptyPublishData = {} as DenmarkPublishInterface;

  const noop = () => {};

  if (!isOpen) return null;

  return (
    <DenmarkEditModal
      isOpen={isOpen}
      onClose={onClose}
      publishData={emptyPublishData}
      hosts={hosts}
      hostOptions={hostOptions}
      denmarkHosts={denmarkHosts}
      handleDenmarkchange={noop}
      handleHostChangeFromSelect={handleHostChange}
      selectedHost={selectedHost}
      currentMediaDetailsForEdit={null}
      customFetchHostSection={handleFetchHostSection}
      disabled={false}
      hostSubsection={hostSubsection}
      currentSelectedSection={[]}
      selectedSections={[]}
      handlecancelOrDelete={noop}
      denmarkTaxonomyList={denmarkTaxonomyList}
      getDenmarkTaxonomyList={(params: any) =>
        dispatch(getDenmarkTaxonomyList(params) as any)
      }
      getDenmarkSectionList={(params: any) =>
        dispatch(getDenmarkSectionList(params) as any)
      }
      getDenmarkChildSectionList={(params: any) =>
        dispatch(getDenmarkChildSectionList(params) as any)
      }
      denmarkSectionList={denmarkSectionList}
      editedSections={[]}
      openPriorityPanel={true}
      fetchPriorityList={(params: any) =>
        dispatch(fetchPriorityList(params) as any)
      }
      priorityList={priorityList}
      priorityOnlyMode={true}
    />
  );
};

export default DenmarkPriorityModal;
